import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Card, InputAdornment, Stack, TextField, Typography, Menu, MenuItem } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import SettingsIcon from '@mui/icons-material/Settings';
import IconButton from '@mui/material/IconButton';
import CustomerService from '@api/Customers/CustomerCtrl';
import { Customer } from '@api/Customers/Customer';
import { showAlertMessage } from '@utils/Alerts/Message';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';
import { ModalAddCustomer } from '@components/Modals/ModalAddCustomer';
import useOpenModal from '@hooks/useOpenModal';
import { hasPermission } from '@utils/Permissions';
import { ProfileTypes } from '@api/Users/User';
import { ModalAddPoints } from '@components/Modals/ModalAddPoints';
import SearchIcon from '@mui/icons-material/Search';
import CustomButton from '@components/Utilities/Buttons/CustomButton';
import Grid from '@mui/material/Grid';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { ModalPointsHistory } from '@components/Modals/ModalPointsHistory';
import { toDateSafe } from '@utils/Date';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setActivityDrawer } from '@store/appStore';
import { buildCustomerOrigin } from '@api/ActivityLogs/CustomerActivity';
import { requestCrmSync } from '@api/Customers/CrmSyncClient';
import { getLifetimePoints } from '@api/Customers/LifetimePoints';
import { idbGet, idbSet } from '@utils/IdbCache';
import * as XLSX from 'xlsx';
import { useDebounce } from 'use-debounce';
import { startOfDay, endOfDay, format } from 'date-fns';
import RangeDatePicker from '@components/Utilities/DatePicker/RangeDatePicker';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// ⚠️ COSTO DEL EXCEL: exportar lee la colección `customers` COMPLETA — una
// lectura de Firestore por cliente, ~14 mil (regla 2 del CLAUDE.md raíz). El
// límite viejo contaba DESCARGAS (2/día) y no ahorraba nada: cada una releía
// todo. Hoy (2026-09-07):
//  1) cada lectura completa se guarda en IndexedDB y sirve para volver a bajar
//     el archivo GRATIS el resto del día;
//  2) lo que se limita por dispositivo (localStorage) son las LECTURAS
//     completas, no las descargas.
const EXPORT_LIMIT_KEY = 'customer_export_limit';
const MAX_FULL_READS_PER_DAY = 2;
const EXPORT_CACHE_KEY = 'customers-export';

interface ExportRow {
    Id: string;
    Nombre: string;
    Telefono: string;
    Email: string;
    Direccion: string;
    FechaRegistro: string;
    PuntosActuales: number;
    PuntosHistoricos: number;
    TotalTransacciones: number;
}

const buildExportRows = (allCustomers: Customer[]): ExportRow[] =>
    allCustomers.map((c) => {
        const addresses = c.profile?.addresses || [];
        const mainAddress = addresses.find((a: any) => a.isDefault) || addresses[0];
        return {
            Id: c.uid || '',
            Nombre: c.profile?.name || 'Sin nombre',
            Telefono: c.phone || '',
            Email: c.email || 'No registrado',
            Direccion: mainAddress?.formattedAddress || 'Sin dirección',
            FechaRegistro: toDateSafe(c.createdAt)?.toLocaleDateString() || '',
            PuntosActuales: c.profile?.wallet?.currentPoints || 0,
            // Lo recibido en toda su vida, sin restar canjes (mismo dato que
            // viaja al CRM como `contact.puntos_totales`).
            PuntosHistoricos: getLifetimePoints(c),
            TotalTransacciones: c.profile?.wallet?.transactions?.length || 0
        };
    });

const ListCustomersScreen: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const branchOffices = useSelector((state: RootReducer) => state.krispis.branchOffices);
    const customerService = useMemo(() => new CustomerService(), []);
    const [totalCustomers, setTotalCustomers] = useState(500);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [lastCustomer, setLastCustomer] = useState<QueryDocumentSnapshot | undefined>();
    const [currentCustomer, setCurrentCustomer] = useState<Customer | undefined>(undefined);
    const [search, setSearch] = useState<string>('');
    const [debouncedSearch] = useDebounce(search, 1000);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0
    });

    const lastCustomerRef = useRef(lastCustomer);
    const paginationModelRef = useRef(paginationModel);
    const isFilterChangingRef = useRef(false);

    useEffect(() => {
        lastCustomerRef.current = lastCustomer;
    }, [lastCustomer]);

    useEffect(() => {
        paginationModelRef.current = paginationModel;
    }, [paginationModel]);
    const [startDate, setStartDate] = useState<Date>(startOfDay(new Date()));
    const [endDate, setEndDate] = useState<Date>(endOfDay(new Date()));

    const modalConfirmation = useConfirmationModal();
    const modalAddCustomer = useOpenModal();
    const modalAddPoints = useOpenModal();
    const modalPointsHistory = useOpenModal();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: Customer) => {
        setAnchorEl(event.currentTarget);
        setSelectedCustomer(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCustomer(null);
    };

    const handleView = () => {
        if (selectedCustomer) {
            onEditCustomer(selectedCustomer); // "Ver" reutiliza el modal de edición/visualización
        }
        handleMenuClose();
    };

    const handleEdit = () => {
        if (selectedCustomer) {
            onEditCustomer(selectedCustomer);
        }
        handleMenuClose();
    };

    const handleAddPoints = () => {
        if (selectedCustomer) {
            onAddPointsToCustomer(selectedCustomer);
        }
        handleMenuClose();
    };

    const handleHistory = () => {
        if (selectedCustomer) {
            onShowPointsHistory(selectedCustomer);
        }
        handleMenuClose();
    };

    const handleDelete = () => {
        if (selectedCustomer) {
            onConfirmDeleteCustomer(selectedCustomer);
        }
        handleMenuClose();
    };

    /**
     * La bitácora del cliente (M4). ⚠️ Snapshot de `selectedCustomer` ANTES de
     * cerrar el menú, que lo pone en null — y `selectedCustomer`, nunca
     * `currentCustomer`: es la lección del bug del 2026-08-31.
     *
     * El primer renglón se DERIVA de lo que el cliente ya sabe de sí mismo (D8):
     * `createdAt` está en 14,957 de 14,959 clientes. **Cero lecturas**: se arma
     * con la fila que la tabla ya tiene en memoria.
     */
    const handleActivity = () => {
        const customer = selectedCustomer;
        handleMenuClose();
        if (!customer?.uid) return;
        const sucursal = branchOffices?.find((branch) => branch.id === customer.joinedFromBranchId)?.name;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'customer',
                sectionId: customer.uid,
                derived: buildCustomerOrigin(customer, sucursal)
            })
        );
    };

    // --- Excel ---
    const todayKey = () => new Date().toDateString();
    const getFullReadsUsedToday = (): number => {
        try {
            const stored = localStorage.getItem(EXPORT_LIMIT_KEY);
            if (!stored) return 0;
            const parsed = JSON.parse(stored);
            return parsed?.date === todayKey() ? Number(parsed.count) || 0 : 0;
        } catch {
            return 0;
        }
    };
    const registerFullRead = () => {
        localStorage.setItem(
            EXPORT_LIMIT_KEY,
            JSON.stringify({ date: todayKey(), count: getFullReadsUsedToday() + 1 })
        );
    };
    const downloadRows = (rows: ExportRow[]) => {
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Clientes');
        XLSX.writeFile(workbook, `Clientes_Krispis_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    /**
     * Con copia de hoy y lecturas disponibles, quien exporta elige: releer
     * (datos frescos, cuesta) o usar la copia (gratis). Aceptar = releer, para
     * que cerrar el diálogo con Escape nunca dispare la lectura cara.
     */
    const onExportExcel = async () => {
        const cached = await idbGet<{ savedAt: number; rows: ExportRow[] }>(EXPORT_CACHE_KEY).catch(() => undefined);
        const cacheIsToday = !!cached && new Date(cached.savedAt).toDateString() === todayKey();
        const readsLeft = MAX_FULL_READS_PER_DAY - getFullReadsUsedToday();
        const hora = cacheIsToday ? format(new Date(cached!.savedAt), 'HH:mm') : '';

        let useCache = false;
        if (cacheIsToday && readsLeft <= 0) {
            useCache = true;
        } else if (cacheIsToday) {
            const releer = window.confirm(
                `Ya se leyó toda la base de clientes hoy a las ${hora}.\n\n` +
                    `Aceptar: volver a leerla para datos frescos (una lectura por cliente; te quedan ${readsLeft} hoy).\n` +
                    `Cancelar: descargar la copia de las ${hora} sin costo.`
            );
            useCache = !releer;
        } else if (readsLeft <= 0) {
            showAlertError(
                `Hoy ya se leyó toda la base ${MAX_FULL_READS_PER_DAY} veces desde este equipo y no hay copia guardada. Intenta mañana.`
            );
            return;
        } else {
            const ok = window.confirm(
                'Exportar lee la base COMPLETA de clientes (una lectura de Firestore por cliente) y puede tardar unos minutos.\n\n' +
                    `Te quedan ${readsLeft} lecturas completas hoy; la copia se guarda para volver a descargarla sin costo.\n\n` +
                    '¿Continuar?'
            );
            if (!ok) return;
        }

        showLoader();
        try {
            let rows: ExportRow[];
            if (useCache && cached) {
                rows = cached.rows;
            } else {
                const allCustomers = await customerService.getCustomers();
                rows = buildExportRows(allCustomers);
                registerFullRead();
                await idbSet(EXPORT_CACHE_KEY, { savedAt: Date.now(), rows }).catch(() => undefined);
            }
            downloadRows(rows);
            showAlertMessage(useCache ? `Excel generado con la copia de hoy (${hora})` : 'Excel generado exitosamente');
        } catch (error) {
            console.error(error);
            showAlertError('Error al generar el Excel');
        } finally {
            hideLoader();
        }
    };
    // ------------------------

    const onDeleteCustomer = async () => {
        showLoader();
        try {
            if (currentCustomer?.uid) {
                await customerService.deleteCustomer(currentCustomer.uid);
                showAlertMessage('Cliente eliminado exitosamente');
                await getData();
            }
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onShowPointsHistory = (customer: Customer) => {
        setCurrentCustomer(customer);
        modalPointsHistory.toggle();
    };

    const onAddPointsToCustomer = (customer: Customer) => {
        setCurrentCustomer(customer);
        modalAddPoints.toggle();
    };

    const onConfirmDeleteCustomer = (customer: Customer) => {
        setCurrentCustomer(customer);
        modalConfirmation.setModalData(
            'Eliminar cliente',
            `Estas seguro de que deseas eliminar al cliente "${customer.profile?.name}"?`
        );
    };

    const onEditCustomer = (customer: Customer) => {
        setCurrentCustomer(customer);
        modalAddCustomer.toggle();
    };

    const getData = async () => {
        showLoader();
        try {
            const currentPagination = paginationModelRef.current;
            const currentLastCustomer = lastCustomerRef.current;

            // Always use undefined for lastDoc when on page 0 to prevent stale cursor issues
            const lastDoc = currentPagination.page === 0 ? undefined : currentLastCustomer;

            const { data, count, lastVisible } = await customerService.getCustomersPaginated({
                search: debouncedSearch,
                startDate,
                endDate,
                pageSize: currentPagination.pageSize,
                lastDoc
            });
            setCustomers(data);
            setLastCustomer(lastVisible);
            setTotalCustomers(count);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    /**
     * Sync individual al CRM desde el menú de la fila (pedido de Irving,
     * 2026-08-31). `force`: quien lo toca quiere empujar al cliente aunque el
     * sistema lo crea al día.
     */
    const handleCrmSyncOne = async () => {
        // ⚠️ `selectedCustomer`, NUNCA `currentCustomer` (bug reportado por
        // Irving el 2026-08-31): `selectedCustomer` es la fila cuyo menú se
        // abrió — como leen TODOS los demás handlers de este menú —, mientras
        // que `currentCustomer` solo se llena como efecto de usar otra opción
        // (Ver/Historial/…). Leerlo aquí hacía que el primer clic "no hiciera
        // nada" (y el guard, encima, era silencioso), y que tras ver el
        // historial de A, sincronizar desde el menú de B empujara a A.
        //
        // Snapshot ANTES de cerrar: handleMenuClose pone selectedCustomer en null.
        const customer = selectedCustomer;
        handleMenuClose();
        if (!customer?.uid) {
            showAlertError('No se pudo identificar al cliente: vuelve a abrir el menú de la fila');
            return;
        }
        showLoader();
        try {
            const result = await requestCrmSync(customer.uid);
            if (result.status === 'failed') {
                showAlertError('No se pudo sincronizar al CRM');
            } else if (result.status === 'skipped') {
                showAlertError('El cliente no tiene teléfono: el CRM no tendría cómo encontrarlo');
            } else {
                showAlertMessage('Cliente sincronizado al CRM');
                if (result.warning) showAlertError(result.warning);
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        isFilterChangingRef.current = true;
        setPaginationModel({ pageSize: 10, page: 0 });
        setLastCustomer(undefined);

        const fetchResetData = async () => {
            showLoader();
            try {
                const { data, count, lastVisible } = await customerService.getCustomersPaginated({
                    search: debouncedSearch,
                    startDate,
                    endDate,
                    pageSize: 10,
                    lastDoc: undefined
                });
                setCustomers(data);
                setLastCustomer(lastVisible);
                setTotalCustomers(count);
            } catch (error: any) {
                showAlertError(error);
            }
            hideLoader();
            isFilterChangingRef.current = false;
        };
        void fetchResetData();
    }, [debouncedSearch, startDate, endDate]);

    // Limpiar búsqueda cuando cambian las fechas y hacer fetch inmediato para evitar el delay del debounce
    useEffect(() => {
        if (
            startDate.toDateString() !== new Date().toDateString() ||
            endDate.toDateString() !== new Date().toDateString()
        ) {
            if (search !== '') {
                setSearch('');
                isFilterChangingRef.current = true;

                const fetchResetData = async () => {
                    showLoader();
                    try {
                        const { data, count, lastVisible } = await customerService.getCustomersPaginated({
                            search: '',
                            startDate,
                            endDate,
                            pageSize: 10,
                            lastDoc: undefined
                        });
                        setCustomers(data);
                        setLastCustomer(lastVisible);
                        setTotalCustomers(count);
                    } catch (error: any) {
                        showAlertError(error);
                    }
                    hideLoader();
                    isFilterChangingRef.current = false;
                };
                void fetchResetData();
            }
        }
    }, [startDate, endDate, customerService]);

    useEffect(() => {
        if (search !== '') {
            const defaultStart = startOfDay(new Date());
            const defaultEnd = endOfDay(new Date());
            if (startDate.getTime() !== defaultStart.getTime() || endDate.getTime() !== defaultEnd.getTime()) {
                setStartDate(defaultStart);
                setEndDate(defaultEnd);
            }
        }
    }, [search]);

    useEffect(() => {
        if (isFilterChangingRef.current) {
            return;
        }
        void getData();
    }, [paginationModel.page]);

    const columns = useMemo<ResponsiveGridColDef<Customer>[]>(
        () => [
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Cliente
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.profile?.name}</Typography>
            },
            {
                field: 'phone',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Telefono
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.phone}</Typography>
            },
            {
                field: 'puntos',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Puntos
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography fontWeight="bold" color="primary">
                        {(row.profile?.wallet?.currentPoints || 0).toFixed(2)}
                    </Typography>
                )
            },
            {
                field: 'fechaRegistro',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Fecha Registro
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography>
                        {toDateSafe(row.createdAt)?.toLocaleDateString() || 'N/A'}
                    </Typography>
                )
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} height="100%" spacing={2}>
                <Stack>
                    <Grid container spacing={2} alignItems="center">
                        {/* Buscador */}
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Buscar cliente"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        {/* Filtro por fecha de registro */}
                        <Grid item xs={12} md={3}>
                            <RangeDatePicker
                                fullWidth
                                value={format(startDate, 'dd MMMM, yyy')}
                                onChangeStartDate={(date) => setStartDate(startOfDay(date))}
                                onChangeEndDate={(date) => setEndDate(endOfDay(date))}
                                onChangeRange={(start, end) => {
                                    setStartDate(startOfDay(start));
                                    setEndDate(endOfDay(end));
                                }}
                            />
                        </Grid>
                        {/* Botones Admin y Exportar */}
                        <Grid item xs={12} md={6}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {/* "Códigos" y el engrane (Recompensas) se quitaron el 2026-09-07
                                    (pedido de Irving): viven en Promociones, en el sidebar. */}
                                {/* El botón del BARRIDO GLOBAL al CRM se quitó el 2026-09-19
                                    (pedido de Irving), una vez corrido. Era de una sola vez:
                                    cada compra sincroniza a su cliente, así que barrer sirve
                                    solo para los inactivos y solo cuando se agrega un campo
                                    nuevo que deba llegarles. El endpoint sigue vivo
                                    (`POST /colegastudio/sync-points`, por tandas con cursor):
                                    si vuelve a hacer falta, se dispara con un script. El
                                    "Sincronizar al CRM" de cada cliente sigue en su menú. */}

                                <CustomButton
                                    fullWidth
                                    variant="outlined"
                                    color="success" // Verde para Excel
                                    startIcon={<FileDownloadIcon />}
                                    onClick={onExportExcel}
                                >
                                    Excel
                                </CustomButton>

                                <CustomButton
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setCurrentCustomer(undefined);
                                        modalAddCustomer.toggle();
                                    }}
                                >
                                    Nuevo
                                </CustomButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>

                <ResponsiveDataGrid
                    getRowId={(row) => `${row.uid}`}
                    rows={customers}
                    columns={columns}
                    onCellDoubleClick={(cel) => onEditCustomer(cel.row as Customer)}
                    rowCount={totalCustomers}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                />
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <MenuItem onClick={handleView}>Ver</MenuItem>
                    <MenuItem onClick={handleEdit}>Editar</MenuItem>
                    <MenuItem onClick={handleAddPoints}>Otorgar Puntos</MenuItem>
                    <MenuItem onClick={handleHistory}>Historial</MenuItem>
                    <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                    {hasPermission([ProfileTypes.Admin]) && (
                        <MenuItem onClick={handleCrmSyncOne}>Sincronizar al CRM</MenuItem>
                    )}
                    {hasPermission([ProfileTypes.Admin]) && (
                        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                            Eliminar
                        </MenuItem>
                    )}
                </Menu>

                {/* MODALES */}
                <ModalAddCustomer
                    modalAddCustomer={modalAddCustomer}
                    customer={currentCustomer}
                    onAddCustomer={getData}
                />

                {/* Ahora pasamos currentCustomer al modal de puntos */}
                <ModalAddPoints modalAddPoints={modalAddPoints} customer={currentCustomer} onAddPoints={getData} />

                <ModalConfirmation
                    modalConfirmation={modalConfirmation}
                    needAuthorization
                    onConfirmation={onDeleteCustomer}
                />

                <ModalPointsHistory modalPointsHistory={modalPointsHistory} selectedCustomer={currentCustomer} />
            </Stack>
        </Box>
    );
};

export default ListCustomersScreen;
